import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * BIPA-aware informed-consent gate for Plaid IDV.
 *
 * Per `docs/security/PLAID_IDV_COMPLIANCE_AUDIT.md` §7.2 (launch option C):
 * the general T&Cs/privacy-policy consent collected on step 0 of the
 * voucher flow is NOT sufficient under Illinois BIPA §15(b), which
 * requires "separately signed" informed consent before biometric
 * collection. This dialog is the separate consent layer.
 *
 * Scope today:
 *   - Discloses that Plaid collects gov ID + selfie + uses facial
 *     recognition to compare them.
 *   - Discloses that Plaid (not DigaVouch) holds the biometric data.
 *   - Discloses our 24-month retention of the verification outcome.
 *   - Links to /biometric-policy (the published retention policy).
 *   - Requires a separate checkbox + an affirmative "Continue" click
 *     before the IDV start endpoint is called.
 *
 * Not in scope yet (queued for the full BIPA-compliance phase):
 *   - Persisting the consent record to the backend with a timestamp +
 *     IP + user agent (the current consent system stores a session-
 *     level consent_id but doesn't differentiate biometric vs general).
 *   - Per-state gating (e.g. refuse to enable IDV for IL/TX/WA users
 *     until a lawyer-reviewed flow lands).
 *   - DSAR forwarding to Plaid when a user later requests deletion.
 *
 * See PLAID_IDV_COMPLIANCE_AUDIT.md §8 for the full pre-prod-flip
 * checklist.
 */

interface PlaidIDVConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user has consented + clicked "Continue". The parent then starts the IDV flow. */
  onConsent: () => void | Promise<void>;
  /**
   * Called when the user clicks "I'd rather not." Per Juan's 2026-06-13
   * decision, refusing consent counts toward the IDV retry budget (same
   * MAX_RETRIES used for actual IDV failures). After the parent ticks
   * the counter past the threshold, it fires /verify/plaid-idv/skip and
   * the gate releases.
   */
  onRefuse?: () => void;
  /** When true, show a spinner on the Continue button (parent is calling /start). */
  starting?: boolean;
}

export function PlaidIDVConsentDialog({
  open,
  onOpenChange,
  onConsent,
  onRefuse,
  starting = false,
}: PlaidIDVConsentDialogProps) {
  const [checked, setChecked] = useState(false);

  // Reset the checkbox whenever the dialog closes — covers ALL close
  // paths: shadcn Dialog's onOpenChange (clicking X, click-outside,
  // pressing Esc), AND parent-driven close (when consent succeeds and
  // PlaidIDVButton calls setConsentOpen(false) directly, which bypasses
  // shadcn's handleOpenChange). Pre-2026-06-13 the reset lived only in
  // handleOpenChange below, so the parent-driven close path left the
  // checkbox pre-ticked on re-open — observed on UAT after a Plaid Link
  // cancellation.
  useEffect(() => {
    if (!open) setChecked(false);
  }, [open]);

  const handleOpenChange = (next: boolean) => {
    if (!next) setChecked(false);
    onOpenChange(next);
  };

  const handleRefuse = () => {
    onRefuse?.();
    handleOpenChange(false);
  };

  const canContinue = checked && !starting;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Confirm identity verification</DialogTitle>
          <DialogDescription className="text-center text-xs text-muted-foreground pt-1">
            One moment before we send you to Plaid — please review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-[13px] leading-relaxed text-foreground">
          <p>
            On the next screen, our verification partner{" "}
            <span className="font-semibold">Plaid</span> will:
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Ask you to photograph a government-issued ID</li>
            <li>Ask you to take a quick selfie</li>
            <li>
              Use <span className="font-semibold">facial recognition</span> to
              compare the two so we can confirm it's really you
            </li>
          </ul>
          <p className="text-muted-foreground">
            <span className="font-semibold text-foreground">Plaid holds the biometric data</span>,
            not DigaVouch. We only receive the outcome — verified or not — and
            keep it for <span className="font-semibold">24 months</span>. You can
            request deletion at any time by emailing{" "}
            <a href="mailto:privacy@digavouch.com" className="underline">
              privacy@digavouch.com
            </a>
            .
          </p>
          <p className="text-[11px] text-muted-foreground">
            Full details:{" "}
            <Link
              to="/biometric-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              biometric retention policy
            </Link>
            .
          </p>
        </div>

        <label className="flex items-start gap-2.5 rounded-lg border border-border bg-card p-3 cursor-pointer">
          <Checkbox
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            disabled={starting}
            className="mt-0.5"
            aria-label="I consent to the collection and use of my biometric data as described"
          />
          <span className="text-[13px] leading-snug text-foreground">
            I consent to the collection and use of my biometric data as described.
          </span>
        </label>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            type="button"
            onClick={() => void onConsent()}
            disabled={!canContinue}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
          >
            {starting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {starting ? "Starting…" : "Continue to Plaid"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={handleRefuse}
            disabled={starting}
            className="w-full"
          >
            I'd rather not
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
